import type { PageModule } from "./page-module"

/**
 * トップFVヒーロー動画の「16:9バンド＋上下の水面反射」を canvas で描画（SPのみ）。
 *
 * バンドを <video>、反射を canvas にすると、<video>（コンポジタ直描画）と canvas（drawImage が1フレーム遅れて合成）で
 * 表示フレームがズレる＝反射がバンドに遅延する。そこで **バンドも canvas** にして、バンド・反射を同じ描画tickで描く
 * ＝全て同一フレーム＝遅延0。反射のズレ・継ぎ目も無し。
 * また -webkit-box-reflect は iOS Safari の <video> に効かず Android でも乱れるため、この canvas 方式が唯一クロスブラウザで成立する。
 *
 * <video> は「描画ソース」。iOS は display:none だと再生を止めるので隠さず（バンド canvas が上から覆って表示）、再生させ続ける。
 * 色/暗転は CSS の mask、下反射のぼかしは CSS の backdrop-filter（&__media::before/::after）が担当。
 */
export const mountHeroReflect: PageModule = (scope) => {
  const video = scope.querySelector<HTMLVideoElement>(".hero__video")
  const band = scope.querySelector<HTMLCanvasElement>(".hero__band")
  const reflects = Array.from(scope.querySelectorAll<HTMLCanvasElement>(".hero__reflect"))
  if (!video || (!band && !reflects.length)) return

  /** 描画対象。バンド＝等倍・高解像 / 反射＝上下反転・低解像（ぼかす/暗くするので十分＝軽量） */
  type Target = { ctx: CanvasRenderingContext2D; w: number; h: number; flip: boolean }
  const targets: Target[] = []
  const add = (canvas: HTMLCanvasElement | null, w: number, h: number, flip: boolean): void => {
    if (!canvas) return
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (ctx) targets.push({ ctx, w, h, flip })
  }
  add(band, 1280, 720, false) // バンド（メイン表示）＝native相当で鮮明
  reflects.forEach((c) => add(c, 640, 360, true))

  const spMq = window.matchMedia("(max-width: 1024px)")
  const rvfc = "requestVideoFrameCallback" in video
  let alive = true
  let handle = 0

  // ヒーローが画面外へスクロールされたら描画を止める（モバイル電池対策）
  let onScreen = true
  const mediaEl = video.closest<HTMLElement>(".hero__media") ?? video
  const io = new IntersectionObserver(([e]) => {
    onScreen = e.isIntersecting
  })
  io.observe(mediaEl)

  /** 動画の現在フレームを、バンド＝等倍／反射＝上下反転で、同tickに全 canvas へ描画（＝全て同一フレーム） */
  const draw = (): void => {
    if (!alive) return
    // SPのみ・画面内・再生可能なときだけ描画（PC / 画面外 / 動画未ロード時は drawImage しない）
    if (onScreen && spMq.matches && video.readyState >= 2 && video.videoWidth > 0) {
      for (const t of targets) {
        if (t.flip) t.ctx.setTransform(1, 0, 0, -1, 0, t.h) // Y反転＝鏡像
        else t.ctx.setTransform(1, 0, 0, 1, 0, 0) // 等倍
        t.ctx.drawImage(video, 0, 0, t.w, t.h)
      }
    }
    schedule()
  }

  /** 新フレームのみ発火する requestVideoFrameCallback を優先（省電力）。無ければ rAF */
  const schedule = (): void => {
    handle = rvfc
      ? (video as HTMLVideoElement & { requestVideoFrameCallback: (cb: () => void) => number }).requestVideoFrameCallback(draw)
      : requestAnimationFrame(draw)
  }

  schedule()

  return () => {
    alive = false
    io.disconnect()
    if (rvfc) {
      ;(video as HTMLVideoElement & { cancelVideoFrameCallback: (h: number) => void }).cancelVideoFrameCallback(handle)
    } else {
      cancelAnimationFrame(handle)
    }
  }
}
